import React, { useState } from 'react';
import { COMPANY_TYPES, CompanyType, type ProfileType, City } from '../../types/profile';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { useProfileStore } from '../../stores/profileStore';
import { useAuth } from '../../context/auth';
import { useServiceCategorySelector } from '../../hooks/useServiceCategorySelector';
import { ROLES } from '../../types/auth';
import { useIndustrySelector } from '../../hooks/useIndustrySelector';
import { FreelancerForm } from './forms/FreelancerForm';
import { RecruiterForm } from './forms/RecruiterForm';
import { CompanyTypeForm } from './forms/CompanyTypeForm';
import { useCitySelector } from '../../hooks/useCitySelector';

interface CompleteProfileFormProps {
  onBack?: () => void;
  onComplete?: () => void;
  initialProfileType?: ProfileType;
}

export default function CompleteProfileForm({
  onBack,
  onComplete,
  initialProfileType,
}: CompleteProfileFormProps) {
  const { t } = useTranslation('auth');
  const { completeProfile } = useProfileStore();
  const { user } = useAuth();
  const { profile } = useProfileStore();

  // State for form fields
  const [firstName, setFirstName] = useState(profile?.first_name || user?.user_metadata?.firstName);
  const [lastName, setLastName] = useState(profile?.last_name || '');
  const [languages, setLanguages] = useState<string[]>(profile?.languages || []);
  const [description, setDescription] = useState(profile?.bio || '');
  const [profileType, setProfileType] = useState<ProfileType>(
    initialProfileType || (profile?.role as ProfileType) || ROLES.FREELANCER
  );
  const [saving, setSaving] = useState(false);

  const [companyType, setCompanyType] = useState<CompanyType | undefined>(profile?.company_type);
  const [companyName, setCompanyName] = useState(profile?.company_name || '');
  const [jobTitle, setJobTitle] = useState(profile?.job_title || '');
  const [organizationWebsite, setOrganizationWebsite] = useState(
    profile?.organization_website || ''
  );
  const [organizationNumber, setOrganizationNumber] = useState(profile?.organization_number || '');
  const [phone, setPhone] = useState(profile?.phone || '');

  // Error state for validation
  const [errors, setErrors] = useState<{
    jobTitle?: string;
    companyName?: string;
    industries?: string;
    firstName?: string;
    lastName?: string;
    city?: string;
    phone?: string;
    services?: string;
    languages?: string;
    organizationIdentity?: string;
  }>({});

  // City store
  const {
    selectedOptions: selectedCities,
    setSelectedOptions: setSelectedCities,
    options: cityOptions,
  } = useCitySelector(profile?.city ? [profile.city] : []);

  const selectedCity = selectedCities[0];

  const {
    selectedOptions: selectedServiceCategories,
    setSelectedOptions: setSelectedServiceCategories,
  } = useServiceCategorySelector(profile?.services);

  const { selectedOptions: selectedIndustries, setSelectedOptions: setSelectedIndustries } =
    useIndustrySelector(profile?.industries);

  const setSelectedCity = (city: City | null) => {
    setSelectedCities(city ? [city] : []);
    if (city && errors.city) setErrors(prev => ({ ...prev, city: undefined }));
  };

  //Validation handlers
  const validateFreelancer = () => {
    const newErrors: typeof errors = {};
    if (!firstName.trim()) newErrors.firstName = t('fieldRequired');
    if (!lastName?.trim()) newErrors.lastName = t('fieldRequired');
    if (!selectedCity) newErrors.city = t('fieldRequired');
    if (!phone.trim()) newErrors.phone = t('fieldRequired');
    if (!selectedServiceCategories.length) newErrors.services = t('fieldRequired');
    if (!languages.length) newErrors.languages = t('fieldRequired');
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateIndividualRecruiter = () => {
    const newErrors: typeof errors = {};
    if (!firstName.trim()) newErrors.firstName = t('fieldRequired');
    if (!lastName?.trim()) newErrors.lastName = t('fieldRequired');
    if (!selectedCity) newErrors.city = t('fieldRequired');
    if (!phone.trim()) newErrors.phone = t('fieldRequired');

    if (!languages.length) newErrors.languages = t('fieldRequired');
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateCompanyRecruiter = () => {
    const newErrors: typeof errors = {};
    if (!firstName.trim()) newErrors.firstName = t('fieldRequired');
    if (!lastName?.trim()) newErrors.lastName = t('fieldRequired');
    if (!selectedCity) newErrors.city = t('fieldRequired');
    if (!phone.trim()) newErrors.phone = t('fieldRequired');
    if (!companyName?.trim()) newErrors.companyName = t('fieldRequired');
    if (!selectedIndustries.length) newErrors.industries = t('fieldRequired');
    if (!languages.length) newErrors.languages = t('fieldRequired');
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateOrganizationRecruiter = () => {
    const newErrors: typeof errors = {};
    if (!firstName.trim()) newErrors.firstName = t('fieldRequired');
    if (!lastName?.trim()) newErrors.lastName = t('fieldRequired');
    if (!selectedCity) newErrors.city = t('fieldRequired');
    if (!phone.trim()) newErrors.phone = t('fieldRequired');
    if (!companyName?.trim()) newErrors.companyName = t('fieldRequired');
    if (!jobTitle?.trim()) newErrors.jobTitle = t('fieldRequired');
    if (!organizationWebsite.trim() && !organizationNumber.trim()) {
      newErrors.organizationIdentity = t('organizationIdentityRequired');
    }
    if (!selectedIndustries.length) newErrors.industries = t('fieldRequired');
    if (!languages.length) newErrors.languages = t('fieldRequired');
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validate = () => {
    if (profileType === ROLES.FREELANCER) return validateFreelancer();
    if (profileType === ROLES.RECRUITER && companyType === COMPANY_TYPES.INDIVIDUAL)
      return validateIndividualRecruiter();
    if (profileType === ROLES.RECRUITER && companyType === COMPANY_TYPES.COMPANY)
      return validateCompanyRecruiter();
    if (profileType === ROLES.RECRUITER && companyType === COMPANY_TYPES.ORGANIZATION)
      return validateOrganizationRecruiter();
  };

  // Submit handler
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate() || !user) return;
    const isIndividual =
      profileType === ROLES.RECRUITER && companyType === COMPANY_TYPES.INDIVIDUAL;
    try {
      setSaving(true);

      await completeProfile({
        userId: user.id,
        role: profileType,
        companyType,
        firstName,
        lastName,
        languages,
        bio: description,
        jobTitle,
        companyName,
        organizationWebsite,
        organizationNumber,
        cityId: selectedCity?.id,
        phone,
        industries: isIndividual ? [] : selectedIndustries.map(line => Number(line.id)),
        serviceIds: selectedServiceCategories.map(line => Number(line.id)),
      });

      toast.success(t('profileUpdateSuccess'));
      if (typeof onComplete === 'function') onComplete();
    } catch (err) {
      console.error('Error completing profile:', err);
      toast.error(t('profileUpdateError'));
    } finally {
      setSaving(false);
    }
  };

  const renderSelectCompanyType = () => {
    return (
      <CompanyTypeForm
        firstName={firstName}
        profileType={profileType}
        onProfileTypeChange={setProfileType}
        onCompanyTypeSelect={setCompanyType}
        onBack={onBack}
        onSubmit={handleSubmit}
        isSubmitting={saving}
      />
    );
  };

  const renderRecruiterForm = () => {
    if (!companyType) return renderSelectCompanyType();

    return (
      <RecruiterForm
        firstName={firstName}
        lastName={lastName}
        phone={phone}
        cityQuery={selectedCity?.name}
        companyType={companyType}
        companyName={companyName}
        jobTitle={jobTitle}
        organizationWebsite={organizationWebsite}
        organizationNumber={organizationNumber}
        selectedIndustries={selectedIndustries}
        languages={languages}
        errors={errors}
        onFirstNameChange={setFirstName}
        onLastNameChange={setLastName}
        onPhoneChange={(value: string) => {
          setPhone(value);
          if (errors.phone) setErrors(prev => ({ ...prev, phone: undefined }));
        }}
        onCitySelect={setSelectedCity}
        onCompanyNameChange={setCompanyName}
        onJobTitleChange={setJobTitle}
        onOrganizationWebsiteChange={value => {
          setOrganizationWebsite(value);
          if (errors.organizationIdentity) {
            setErrors(prev => ({ ...prev, organizationIdentity: undefined }));
          }
        }}
        onOrganizationNumberChange={value => {
          setOrganizationNumber(value);
          if (errors.organizationIdentity) {
            setErrors(prev => ({ ...prev, organizationIdentity: undefined }));
          }
        }}
        onIndustriesChange={setSelectedIndustries}
        onCompanyTypeChange={setCompanyType}
        onLanguagesChange={setLanguages}
        cityOptions={cityOptions}
        onBack={onBack}
        onSubmit={handleSubmit}
        isSubmitting={saving}
      />
    );
  };

  if (profileType === ROLES.FREELANCER) {
    return (
      <FreelancerForm
        firstName={firstName}
        lastName={lastName}
        phone={phone}
        cityQuery={selectedCity?.name}
        languages={languages}
        description={description}
        selectedServiceCategories={selectedServiceCategories}
        errors={errors}
        onFirstNameChange={setFirstName}
        onLastNameChange={setLastName}
        onPhoneChange={(value: string) => {
          setPhone(value);
          if (errors.phone) setErrors(prev => ({ ...prev, phone: undefined }));
        }}
        onCitySelect={setSelectedCity}
        onLanguagesChange={setLanguages}
        onDescriptionChange={setDescription}
        onServiceCategoriesChange={setSelectedServiceCategories}
        cityOptions={cityOptions}
        onBack={onBack}
        onSubmit={handleSubmit}
        isSubmitting={saving}
      />
    );
  }

  return renderRecruiterForm();
}
